import React, { createContext, FC, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";

import log from "loglevel";
import { useLocalStorage } from "react-use";

import { CookieCategory } from "@/components/features";
import config from "@/config";
import * as tagManager from "@/tagManager";

interface ICookieConsentContext {
    date: Date;
    categories: CookieCategory[];
    isLoadingGTM: boolean;
    setConsent?: (theCategories: CookieCategory[]) => void;
}

// TODO: Move GTM responsability to a separate context and the tagManager.ts to a hook

export const CookieConsentContext = createContext<ICookieConsentContext>({
    date: null,
    categories: [],
    isLoadingGTM: true,
});
CookieConsentContext.displayName = "CookieConsentContext";

export const CookieConsentProvider: FC<PropsWithChildren> = ({ children }) => {
    const [consentCategories, setConsentCategories] = useLocalStorage<CookieCategory[]>("consentCategories", []);
    const [consentDate, setConsentDate] = useLocalStorage<Date>("consentDate", null);
    const [isLoadingGTM, setIsLoadingGTM] = useState(true);

    const isGTMEnabled = consentCategories.includes("performance") && !!config.GOOGLE_TAGMANAGER_ID && !!consentDate;

    useEffect(() => {
        // Initialize Google Analytics if not already initialized
        if (isGTMEnabled && !window["dataLayer"]) {
            log.info("Initialize Google Analytics");
            tagManager.init({
                gtmId: config.GOOGLE_TAGMANAGER_ID,
                auth: config.GOOGLE_TAGMANAGER_AUTH,
                preview: config.GOOGLE_TAGMANAGER_PREVIEW,
            });
        }
    }, [consentCategories]);

    useEffect(() => {
        // If Google Analytics is already initialized or if it is not enabled, stop loading
        if (!isGTMEnabled || !!window["dataLayer"]) {
            setIsLoadingGTM(false);
        }
    }, [window["dataLayer"]]);

    const contextValue = useMemo(
        (): ICookieConsentContext => ({
            date: consentDate,
            categories: consentCategories,
            isLoadingGTM,
            setConsent(theCategories: CookieCategory[]) {
                const currentDate = new Date();
                // local storage
                setConsentCategories(theCategories);
                setConsentDate(currentDate);
            },
        }),
        [consentCategories, consentDate, isLoadingGTM],
    );

    return <CookieConsentContext.Provider value={contextValue}>{children}</CookieConsentContext.Provider>;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useCookieConsent = () => {
    return useContext(CookieConsentContext);
};
